/**
 * HealthOverview Component
 *
 * Displays aggregated health scores and metrics for mature clients.
 * Shows repository health, specification status, and overall platform health.
 *
 * Used in adaptive dashboard for clients with completion = 100%.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { Card, Badge, Progress, Tooltip } from 'flowbite-react';
import {
  HiShieldCheck,
  HiExclamation,
  HiCheckCircle,
  HiClock,
  HiTrendingUp,
  HiTrendingDown,
  HiMinus,
  HiCode,
  HiDocumentText,
  HiLightningBolt,
  HiChartBar,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';

// =============================================================================
// TYPES
// =============================================================================

export interface RepositoryHealth {
  repository: string;
  health_score: number;
  concerns_count: number;
  last_scan_at: string | null;
}

export interface SpecificationHealth {
  total_specs: number;
  approved_specs: number;
  draft_specs: number;
  outdated_specs: number;
  avg_approval_time_days: number;
}

export interface WorkflowHealth {
  total_executions_today: number;
  success_rate: number;
  avg_execution_time_seconds: number;
  failed_workflows: number;
}

export interface OverallHealth {
  overall_score: number;
  trend: 'up' | 'down' | 'stable';
  trend_change: number;
  last_updated: string;
}

export interface HealthData {
  overall: OverallHealth;
  repositories: RepositoryHealth[];
  specifications: SpecificationHealth;
  workflows: WorkflowHealth;
}

interface HealthOverviewProps {
  health: HealthData;
  onViewRepositories?: () => void;
  onViewSpecifications?: () => void;
  onViewWorkflows?: () => void;
  compact?: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getHealthColor = (score: number): 'green' | 'yellow' | 'red' => {
  if (score >= 70) return 'green';
  if (score >= 40) return 'yellow';
  return 'red';
};

const getHealthLabel = (score: number): string => {
  if (score >= 70) return 'Healthy';
  if (score >= 40) return 'Needs Attention';
  return 'At Risk';
};

const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return <HiTrendingUp className="w-5 h-5 text-green-600" />;
    case 'down':
      return <HiTrendingDown className="w-5 h-5 text-red-600" />;
    default:
      return <HiMinus className="w-5 h-5 text-gray-500" />;
  }
};

const getTrendColor = (trend: 'up' | 'down' | 'stable'): string => {
  switch (trend) {
    case 'up':
      return 'text-green-600';
    case 'down':
      return 'text-red-600';
    default:
      return 'text-gray-500';
  }
};

// =============================================================================
// COMPONENT
// =============================================================================

export const HealthOverview: React.FC<HealthOverviewProps> = ({
  health,
  onViewRepositories,
  onViewSpecifications,
  onViewWorkflows,
  compact = false,
}) => {
  const overallColor = getHealthColor(health.overall.overall_score);

  // Compact view
  if (compact) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${
              overallColor === 'green' ? 'bg-green-100' :
              overallColor === 'yellow' ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <HiShieldCheck className={`w-6 h-6 ${
                overallColor === 'green' ? 'text-green-600' :
                overallColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'
              }`} />
            </div>
            <div>
              <div className="text-sm text-gray-500">Platform Health</div>
              <div className="text-2xl font-bold text-gray-900">
                {health.overall.overall_score}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {getTrendIcon(health.overall.trend)}
            <span className={`text-sm font-medium ${getTrendColor(health.overall.trend)}`}>
              {health.overall.trend_change > 0 ? '+' : ''}{health.overall.trend_change}%
            </span>
          </div>
        </div>
      </Card>
    );
  }

  // Full view
  return (
    <div className="space-y-6">
      {/* Overall Health Card */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Platform Health</h2>
          <span className="text-xs text-gray-500">
            Updated {new Date(health.overall.last_updated).toLocaleTimeString()}
          </span>
        </div>

        <div className="flex items-center gap-6 mb-6">
          {/* Health Score Circle */}
          <div className={`relative w-24 h-24 flex items-center justify-center rounded-full border-4 ${
            overallColor === 'green' ? 'border-green-500 bg-green-50' :
            overallColor === 'yellow' ? 'border-yellow-500 bg-yellow-50' : 'border-red-500 bg-red-50'
          }`}>
            <div className="text-center">
              <div className={`text-3xl font-bold ${
                overallColor === 'green' ? 'text-green-600' :
                overallColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {health.overall.overall_score}
              </div>
              <div className="text-xs text-gray-500">Score</div>
            </div>
          </div>

          {/* Health Details */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge color={overallColor} size="lg">
                {getHealthLabel(health.overall.overall_score)}
              </Badge>
              <div className="flex items-center gap-1">
                {getTrendIcon(health.overall.trend)}
                <span className={`text-sm font-medium ${getTrendColor(health.overall.trend)}`}>
                  {health.overall.trend_change > 0 ? '+' : ''}{health.overall.trend_change}% from last week
                </span>
              </div>
            </div>
            <Progress
              progress={health.overall.overall_score}
              color={overallColor}
              size="lg"
            />
          </div>
        </div>

        {/* Health Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Repository Health */}
          <div
            className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={onViewRepositories}
          >
            <div className="flex items-center gap-2 mb-3">
              <FaGithub className="w-5 h-5 text-gray-700" />
              <span className="font-medium text-gray-900">Repositories</span>
            </div>
            <div className="space-y-2">
              {health.repositories.length === 0 ? (
                <p className="text-sm text-gray-500">No repositories connected</p>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Connected</span>
                    <span className="font-medium">{health.repositories.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Avg Health</span>
                    <span className="font-medium">
                      {Math.round(
                        health.repositories.reduce((sum, r) => sum + r.health_score, 0) /
                        health.repositories.length
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Concerns</span>
                    <span className={`font-medium ${
                      health.repositories.reduce((sum, r) => sum + r.concerns_count, 0) > 0
                        ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {health.repositories.reduce((sum, r) => sum + r.concerns_count, 0)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Specification Health */}
          <div
            className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={onViewSpecifications}
          >
            <div className="flex items-center gap-2 mb-3">
              <HiDocumentText className="w-5 h-5 text-indigo-600" />
              <span className="font-medium text-gray-900">Specifications</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total</span>
                <span className="font-medium">{health.specifications.total_specs}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Approved</span>
                <span className="font-medium text-green-600">
                  {health.specifications.approved_specs}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Drafts</span>
                <span className={`font-medium ${
                  health.specifications.draft_specs > 0 ? 'text-yellow-600' : ''
                }`}>
                  {health.specifications.draft_specs}
                </span>
              </div>
              {health.specifications.outdated_specs > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Outdated</span>
                  <span className="font-medium text-red-600">
                    {health.specifications.outdated_specs}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Workflow Health */}
          <div
            className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={onViewWorkflows}
          >
            <div className="flex items-center gap-2 mb-3">
              <HiLightningBolt className="w-5 h-5 text-amber-500" />
              <span className="font-medium text-gray-900">Workflows</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Today</span>
                <span className="font-medium">
                  {health.workflows.total_executions_today}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Success Rate</span>
                <span className={`font-medium ${
                  health.workflows.success_rate >= 95 ? 'text-green-600' :
                  health.workflows.success_rate >= 80 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {health.workflows.success_rate}%
                </span>
              </div>
              {health.workflows.failed_workflows > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Failed</span>
                  <span className="font-medium text-red-600">
                    {health.workflows.failed_workflows}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Repository Health Details */}
      {health.repositories.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Repository Health</h3>
            {onViewRepositories && (
              <button
                onClick={onViewRepositories}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                View All
              </button>
            )}
          </div>
          <div className="space-y-3">
            {health.repositories.slice(0, 5).map((repo) => {
              const repoColor = getHealthColor(repo.health_score);
              return (
                <div
                  key={repo.repository}
                  className="flex items-center justify-between py-2 border-b last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <FaGithub className="w-4 h-4 text-gray-500" />
                    <span className="font-medium text-gray-900">{repo.repository}</span>
                    {repo.concerns_count > 0 && (
                      <Tooltip content={`${repo.concerns_count} concerns found`}>
                        <Badge color="warning" size="xs">
                          <HiExclamation className="w-3 h-3 mr-1 inline" />
                          {repo.concerns_count}
                        </Badge>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24">
                      <Progress progress={repo.health_score} color={repoColor} size="sm" />
                    </div>
                    <Badge color={repoColor} size="sm">
                      {repo.health_score}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default HealthOverview;
